import { occRootOf } from "@/lib/parse-utils";
import { OPTION_MULTIPLIER } from "@/lib/option-multiplier";

/**
 * Cross-strategy position classifier.
 *
 * Hierarchical classification of raw broker positions:
 * 1. Covered Call pass — short calls paired with sufficient equity shares
 * 2. Spread pass — remaining short options paired with matching long options
 * 3. Remainder — leftover equities and unpaired options
 */

export interface ClassifiedEquity {
  symbol: string;
  shares: number;
  avgCost: number;
  marketValue: number;
}

export interface ClassifiedOption {
  optionSymbol: string;
  underlying: string;
  putCall: string;
  description: string;
  strike: number;
  expiration: string;
  contracts: number;
  isShort: boolean;
  averagePrice: number;
  marketValue: number;
}

export interface CoveredCallGroup {
  underlying: string;
  equity: ClassifiedEquity;
  shortCall: ClassifiedOption;
  coveredContracts: number;
}

export interface SpreadGroup {
  underlying: string;
  putCall: string;
  expiration: string;
  shortLeg: ClassifiedOption;
  longLeg: ClassifiedOption;
  width: number;
}

export interface ClassifiedPositions {
  coveredCalls: CoveredCallGroup[];
  spreads: SpreadGroup[];
  remainingEquities: ClassifiedEquity[];
  remainingOptions: ClassifiedOption[];
}

export function classifyPositions(
  equities: ClassifiedEquity[],
  options: ClassifiedOption[],
): ClassifiedPositions {
  // Track available shares per equity symbol (mutable copy)
  const availableShares = new Map<string, number>();
  for (const eq of equities) {
    availableShares.set(eq.symbol, (availableShares.get(eq.symbol) ?? 0) + eq.shares);
  }

  // --- Pass 1: Covered Calls ---
  const coveredCalls: CoveredCallGroup[] = [];
  const usedOptionSymbols = new Set<string>();

  const shortCalls = options
    .filter((o) => o.isShort && o.putCall === "CALL")
    .sort((a, b) => a.underlying.localeCompare(b.underlying) || a.strike - b.strike);

  for (const sc of shortCalls) {
    const avail = availableShares.get(sc.underlying) ?? 0;
    const neededShares = sc.contracts * OPTION_MULTIPLIER;
    if (avail >= neededShares) {
      // Find the equity record for this underlying
      const eq = equities.find((e) => e.symbol === sc.underlying);
      if (eq) {
        coveredCalls.push({
          underlying: sc.underlying,
          equity: eq,
          shortCall: sc,
          coveredContracts: sc.contracts,
        });
        availableShares.set(sc.underlying, avail - neededShares);
        usedOptionSymbols.add(sc.optionSymbol);
      }
    }
  }

  // --- Pass 2: Spreads ---
  const remaining = options.filter((o) => !usedOptionSymbols.has(o.optionSymbol));
  const shorts = remaining.filter((o) => o.isShort);
  const longs = remaining.filter((o) => !o.isShort);

  // Index longs by underlying|OCC-root|expiration|putCall.
  // RUT monthlies and RUTW weeklies share $RUT + Friday expiry — they are
  // different listed contracts and must not pair across roots.
  const longIndex = new Map<string, ClassifiedOption[]>();
  for (const l of longs) {
    const key = `${l.underlying}|${occRootOf(l.optionSymbol)}|${l.expiration}|${l.putCall}`;
    const list = longIndex.get(key) ?? [];
    list.push(l);
    longIndex.set(key, list);
  }

  const spreads: SpreadGroup[] = [];
  const usedLongs = new Set<string>();

  for (const s of shorts) {
    const key = `${s.underlying}|${occRootOf(s.optionSymbol)}|${s.expiration}|${s.putCall}`;
    const candidates = longIndex.get(key);
    if (!candidates) continue;

    // For puts: long strike < short strike. For calls: long strike > short strike.
    const isPut = s.putCall === "PUT";
    const match = candidates.find(
      (l) =>
        !usedLongs.has(l.optionSymbol) &&
        (isPut ? l.strike < s.strike : l.strike > s.strike),
    );
    if (!match) continue;

    usedOptionSymbols.add(s.optionSymbol);
    usedOptionSymbols.add(match.optionSymbol);
    usedLongs.add(match.optionSymbol);

    spreads.push({
      underlying: s.underlying,
      putCall: s.putCall,
      expiration: s.expiration,
      shortLeg: s,
      longLeg: match,
      width: Math.abs(s.strike - match.strike),
    });
  }

  // --- Pass 3: Remainder ---
  const remainingOptions = options.filter((o) => !usedOptionSymbols.has(o.optionSymbol));

  // Equities with leftover shares
  const remainingEquities: ClassifiedEquity[] = [];
  for (const eq of equities) {
    const leftover = availableShares.get(eq.symbol) ?? 0;
    if (leftover > 0) {
      remainingEquities.push({ ...eq, shares: leftover });
    }
  }

  return { coveredCalls, spreads, remainingEquities, remainingOptions };
}
